import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableHead,
  TableContainer,
  Switch,
  FormControlLabel,
  Button,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import usePlanStore from '../../../../store/planStore';
import useFetchMultiplePlans from '../MediaPlansTable/ComparedPlansModal/hooks/useFetchMultiplePlans';
import PlansHeaderRow from '../MediaPlansTable/ComparedPlansModal/PlansHeaderRow';
import { AnyPlan } from '../MediaPlansTable/ComparedPlansModal/types';
import GlobalTotalsRow from '../MediaPlansTable/ComparedPlansModal/GlobalTotalsRow';
import {
  formatBudget,
  getCountryData,
  getCountryTotals,
  getDigitalEntry,
  getDigitalRowsForCountry,
  getGlobalTotals,
  getPriorityLabel,
} from '../MediaPlansTable/ComparedPlansModal/helpers';
import CountryAccordion from '../MediaPlansTable/ComparedPlansModal/CountryAccordion/CountryAccordion';
import BackToList from '../Plan/BackToList/BackToList';

export default function ComparedPlansPage() {
  const { comparedPlans } = usePlanStore();
  const [onlyDifferences, setOnlyDifferences] = useState(false);

  const [sp] = useSearchParams();
  const idsFromUrl = (sp.get('ids') ?? '').split(',').filter(Boolean);

  const planIds = useMemo(() => {
    return idsFromUrl.length ? idsFromUrl : Object.keys(comparedPlans);
  }, [idsFromUrl, comparedPlans]);

  const { isLoading } = useFetchMultiplePlans(planIds);

  const plans: AnyPlan[] = useMemo(
    () => planIds.map((id) => comparedPlans[id]).filter(Boolean) as AnyPlan[],
    [planIds, comparedPlans]
  );

  const countries = useMemo<string[]>(() => {
    const set = new Set<string>();
    plans.forEach((p) => {
      Object.keys(p.countrySetup ?? {}).forEach((country) => set.add(country));
    });
    return Array.from(set);
  }, [plans]);

  if (!planIds.length) return <Typography>No plans selected.</Typography>;

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        background: '#fafafa',
        minHeight: 'calc(100vh - 75px)',
        px: 3,
        mb: 6,
      }}
    >
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            p: 3,
          }}
        >
          <BackToList sx={{ maxWidth: '140px' }} goBackOnly />
          <Typography fontWeight={700} fontSize="24px" textAlign="center">
            COMPARE YOUR PLANS
          </Typography>

          {plans.length ? (
            <FormControlLabel
              control={
                <Switch
                  checked={onlyDifferences}
                  onChange={(e) => setOnlyDifferences(e.target.checked)}
                  size="small"
                />
              }
              label="Only differences"
              sx={{ justifySelf: 'end', m: 0 }}
            />
          ) : (
            <Box />
          )}
        </Box>

        {/* Content */}
        {isLoading && !plans.length ? (
          <Stack alignItems="center" mt={2}>
            <CircularProgress />
          </Stack>
        ) : (
          <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <PlansHeaderRow
                  plans={plans}
                  headerBg="white"
                  firstColWidth={243}
                />
              </TableHead>

              <TableBody>
                <GlobalTotalsRow
                  plans={plans}
                  bg="#F2F8FF"
                  formatBudget={formatBudget}
                  getGlobalTotals={getGlobalTotals}
                  firstColWidth={243}
                  showOnlyDifferences={onlyDifferences}
                />

                {countries.map((country) => (
                  <CountryAccordion
                    key={country}
                    country={country}
                    plans={plans}
                    formatBudget={formatBudget}
                    getCountryTotals={getCountryTotals}
                    getCountryData={getCountryData}
                    getDigitalRowsForCountry={getDigitalRowsForCountry}
                    getDigitalEntry={getDigitalEntry}
                    getPriorityLabel={getPriorityLabel}
                    showOnlyDifferences={onlyDifferences}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
